// Copyright 2018 Sorama B.V.

import { WebGLExtensions } from "extensions/WebGLExtensions";
import BasicTextureFragmentShader from "./../../shaders/BasicTextureFragmentShader.shader";
import BasicVertexShader from "./../../shaders/BasicVertexShader.shader";

export class TextureProgram {
    private readonly maximumTextureDimension: number;

    private shaderProgram: WebGLProgram;
    private dataTextureLocation: WebGLUniformLocation;
    private vertexBuffer: WebGLBuffer;

    constructor(gl: WebGLRenderingContext, maximumTextureDimension: number) {
        this.maximumTextureDimension = maximumTextureDimension;
        this.initShader(gl);
        this.initBuffers(gl);
    }

    private initShader(gl: WebGLRenderingContext): void {
        let error: string;

        // Generate shaders
        const vertexShader = gl.createShader(gl.VERTEX_SHADER);
        const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER);

        // Compile shaders
        gl.shaderSource(vertexShader, BasicVertexShader);
        gl.compileShader(vertexShader);

        if (!gl.getShaderParameter(vertexShader, gl.COMPILE_STATUS)) {
            error = gl.getShaderInfoLog(vertexShader);
            throw new Error(error);
        }

        gl.shaderSource(fragmentShader, BasicTextureFragmentShader);
        gl.compileShader(fragmentShader);

        if (!gl.getShaderParameter(fragmentShader, gl.COMPILE_STATUS)) {
            error = gl.getShaderInfoLog(fragmentShader);
            throw new Error(error);
        }

        // Create program
        this.shaderProgram = gl.createProgram();
        gl.attachShader(this.shaderProgram, vertexShader);
        gl.attachShader(this.shaderProgram, fragmentShader);
        gl.linkProgram(this.shaderProgram);

        if (!gl.getProgramParameter(this.shaderProgram, gl.LINK_STATUS)) {
            error = gl.getProgramInfoLog(this.shaderProgram);
            throw new Error(error);
        }

        gl.useProgram(this.shaderProgram);

        this.dataTextureLocation = gl.getUniformLocation(this.shaderProgram, "dataTexture");

        WebGLExtensions.ThrowOnError(gl);
    }

    private initBuffers(gl: WebGLRenderingContext): void {
        // Create a buffer and fill with data
        this.vertexBuffer = gl.createBuffer();
        const vertexData = new Float32Array(8);

        vertexData[0] = -1;
        vertexData[1] = -1;

        vertexData[2] = -1;
        vertexData[3] = 1;

        vertexData[4] = 1;
        vertexData[5] = -1;

        vertexData[6] = 1;
        vertexData[7] = 1;

        gl.bindBuffer(WebGLRenderingContext.ARRAY_BUFFER, this.vertexBuffer);
        gl.bufferData(WebGLRenderingContext.ARRAY_BUFFER, vertexData, WebGLRenderingContext.STATIC_DRAW);

        WebGLExtensions.ThrowOnError(gl);
    }

    public draw(gl: WebGLRenderingContext, texture: WebGLTexture, frameBuffer: WebGLFramebuffer) {
        gl.useProgram(this.shaderProgram);

        // Enable vertex arrays
        gl.enableVertexAttribArray(0);

        // Update buffer data
        gl.bindBuffer(WebGLRenderingContext.ARRAY_BUFFER, this.vertexBuffer);
        gl.vertexAttribPointer(0, 2, WebGLRenderingContext.FLOAT, true, 0, 0);
        gl.bindAttribLocation(this.shaderProgram, 0, "vertexPosition");

        // Bind stuff
        gl.activeTexture(WebGLRenderingContext.TEXTURE0);
        gl.bindTexture(WebGLRenderingContext.TEXTURE_2D, texture);
        gl.uniform1i(this.dataTextureLocation, 0);
        gl.bindFramebuffer(WebGLRenderingContext.FRAMEBUFFER, frameBuffer);

        gl.viewport(0, 0, this.maximumTextureDimension, this.maximumTextureDimension);

        // Draw
        gl.drawArrays(WebGLRenderingContext.TRIANGLE_STRIP, 0, 4);

        // Unbind stuff
        gl.bindTexture(WebGLRenderingContext.TEXTURE_2D, null);
        gl.bindFramebuffer(WebGLRenderingContext.FRAMEBUFFER, null);

        gl.disableVertexAttribArray(0);

        gl.useProgram(null);
    }
}
